import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { DPCompany } from '../models/DPCompany';
import { DPSitePage } from '../models/enums/DPSitePage';
import { DPUserStatus } from '../models/enums/DPUserStatus';
import { DPJob } from '../models/DPJob';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { UtilsService } from './utils.service';
import { CompanyServiceInterface } from './company.service.interface';

/**
 * Company related service
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyService implements CompanyServiceInterface {
  // Company data.
  public readonly info = new ReplaySubject<DPCompany>(1);

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private utils: UtilsService
  ) {}

  /**
   * Get company info.
   */
  getCompanyInfo() {
    return this.api.getCompanyInfo(this.utils.getSubdomain()).pipe(
      tap((company) => {
        this.info.next(company);
      })
    );
  }

  /**
   * Get company jobs.
   */
  public getJobs(force?: boolean) {
    return this.info.pipe(
      filter((info) => !!info),
      switchMap(() =>
        this.api.getJobs(this.auth.companySettingID, {
          force,
        })
      ),
      take(1)
    );
  }

  /**
   * Get company text based on application status
   */
  public getCompanyText(force?: boolean) {
    return this.getJobs(force).pipe(
      switchMap((jobs: DPJob[]) => {
        const completedJob = jobs.find(
          (job) => job.userStatusID === DPUserStatus.Completed
        );
        if (completedJob) {
          return this.api.getCustomText(
            this.auth.companySettingID,
            DPSitePage.JobComplete,
            completedJob.jobID
          );
        } else {
          return this.api.getCustomText(
            this.auth.companySettingID,
            DPSitePage.JobInitial,
            null
          );
        }
      }),
      take(1)
    );
  }

  /**
   * Apply to a job
   */
  public applyToJob(jobID: number) {
    return this.api.applyToJob(jobID);
  }
}
